import { useContext, useEffect } from 'react';

import { useEnableNewHotkeySystem } from '../../feature-flags/hooks/use-enable-new-hotkey-system';
import { Context as GlobalEventListenerContext } from '../components/global-event-listener/context';
import { HIGH_PRIORITY_QUEUE } from '../components/global-event-listener/constants';

type UseGlobalKeyDownOptions = {
  queue?: 'HIGH_PRIORITY_QUEUE' | 'LOW_PRIORITY_QUEUE';
  // When provided in the new-system path, attaches the listener to this element
  // instead of window, enabling a scoped boundary below a stopPropagation wrapper.
  targetRef?: { current: EventTarget | null };
};

export const useGlobalKeyDown = (
  handler: (event: Event) => void,
  { queue = HIGH_PRIORITY_QUEUE, targetRef }: UseGlobalKeyDownOptions = {},
) => {
  const isNewHotkeySystemEnabled = useEnableNewHotkeySystem();
  const { subscribe } = useContext(GlobalEventListenerContext);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const targetEl = targetRef?.current;

  useEffect(() => {
    if (isNewHotkeySystemEnabled) {
      const target = targetEl ?? global.window;
      target.addEventListener('keydown', handler);
      return () => {
        target.removeEventListener('keydown', handler);
      };
    }

    return subscribe('onKeyDown', handler, queue);
  }, [handler, isNewHotkeySystemEnabled, subscribe, queue, targetEl]);
};
